import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { SimProvider } from "@/context/SimContext";
import { AuthProvider } from "@/context/AuthContext";
import { ContentProvider } from "@/context/ContentContext";
import { BillingProvider } from "@/context/BillingContext";
import { RequireAuth } from "@/components/RequireAuth";
import { RequireAdmin } from "@/components/RequireAdmin";
import { SimGate } from "@/components/SimGate";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ScrollToTop } from "@/components/ScrollToTop";
import { UpgradeBanner } from "@/components/UpgradeBanner";
import { BackendHealthBanner } from "@/components/BackendHealthBanner";
import { OfflineBanner } from "@/components/OfflineBanner";
import { ProgressQueueSync } from "@/components/ProgressQueueSync";
import { AdminConfigWarningBanner } from "@/components/AdminConfigWarningBanner";
import { VisitTracker } from "@/components/VisitTracker";
import AuthCallback from "@/components/AuthCallback";
import Landing from "@/pages/Landing";
import FlashcardsHub from "@/pages/FlashcardsHub";
import FlashcardsDeck from "@/pages/FlashcardsDeck";
import CheatSheetsHub from "@/pages/CheatSheetsHub";
import CheatSheetPage from "@/pages/CheatSheetPage";
import Home from "@/pages/Home";
import SectionPage from "@/pages/SectionPage";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import InstructorTeam from "@/pages/InstructorTeam";
import PatientIntake from "@/pages/PatientIntake";
import AnaestheticPlan from "@/pages/AnaestheticPlan";
import Monitor from "@/pages/Monitor";
import PatientIntake2 from "@/pages/PatientIntake2";
import AnaestheticPlan2 from "@/pages/AnaestheticPlan2";
import Monitor2 from "@/pages/Monitor2";
import FluidPump from "@/pages/FluidPump";
import IPPV from "@/pages/IPPV";
import AnaesMachine from "@/pages/AnaesMachine";
import DrugCalc from "@/pages/DrugCalc";
import Triage from "@/pages/Triage";
import Dental from "@/pages/Dental";
import DrugStorage from "@/pages/DrugStorage";
import Nutrition from "@/pages/Nutrition";
import Admission from "@/pages/Admission";
import ClinPath from "@/pages/ClinPath";
import Urinalysis from "@/pages/Urinalysis";
import WardRounds from "@/pages/WardRounds";
import BloodReport from "@/pages/BloodReport";
import GenericSim from "@/pages/GenericSim";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminSubscribers from "@/pages/admin/AdminSubscribers";
import AdminSimulators from "@/pages/admin/AdminSimulators";
import AdminReading from "@/pages/admin/AdminReading";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminErrors from "@/pages/admin/AdminErrors";
import AdminBilling from "@/pages/admin/AdminBilling";
import AdminTeams from "@/pages/admin/AdminTeams";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import BillingSuccess from "@/pages/BillingSuccess";
import { Toaster } from "@/components/ui/sonner";

const guard = (el) => <RequireAuth>{el}</RequireAuth>;
// Entitlement gate for simulator routes. Anonymous users can play free sims;
// SimGate itself decides whether to render, show a sign-in prompt, or paywall.
// Do NOT wrap with RequireAuth here — that would force sign-in on free sims too.
const simGuard = (id, el) => <SimGate simId={id}>{el}</SimGate>;

// Intercept the Google OAuth return (#session_id=...) BEFORE any route/guard runs.
function AuthGate() {
  const location = useLocation();
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      {/* Retired marketing pages — kept redirected so old bookmarks land on
          the single Landing page. Source files preserved at
          /app/frontend/src/pages/_backup_marketing/*.bak — restore them by
          copying back and adding routes if the multi-page marketing site
          is ever re-enabled. */}
      <Route path="/simulators" element={<Navigate to="/" replace />} />
      <Route path="/cert-iv-alignment" element={<Navigate to="/" replace />} />
      <Route path="/for-individuals" element={<Navigate to="/" replace />} />
      <Route path="/for-clinics" element={<Navigate to="/" replace />} />
      <Route path="/for-tafes" element={<Navigate to="/" replace />} />
      <Route path="/about" element={<Navigate to="/" replace />} />
      <Route path="/contact" element={<Navigate to="/" replace />} />
      {/* Pricing hidden while SUBSCRIPTIONS_ENABLED is false — every sim
          is currently free for individuals. */}
      <Route path="/pricing" element={<Navigate to="/" replace />} />
      <Route path="/flashcards" element={<FlashcardsHub />} />
      <Route path="/flashcards/:categoryId" element={<FlashcardsDeck />} />
      <Route path="/cheat-sheets" element={<CheatSheetsHub />} />
      <Route path="/cheat-sheets/:sheetId" element={<CheatSheetPage />} />
      <Route path="/sections" element={<Home />} />
      <Route path="/section/:sectionId" element={<SectionPage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/dashboard" element={guard(<Dashboard />)} />
      <Route path="/team" element={guard(<InstructorTeam />)} />
      <Route path="/billing/success" element={guard(<BillingSuccess />)} />
      <Route path="/intake" element={simGuard("anaesthesia", <PatientIntake />)} />
      <Route path="/plan" element={simGuard("anaesthesia", <AnaestheticPlan />)} />
      <Route path="/monitor" element={simGuard("anaesthesia", <Monitor />)} />
      <Route path="/intake2" element={simGuard("anaesthesia2", <PatientIntake2 />)} />
      <Route path="/plan2" element={simGuard("anaesthesia2", <AnaestheticPlan2 />)} />
      <Route path="/monitor2" element={simGuard("anaesthesia2", <Monitor2 />)} />
      <Route path="/fluid-pump" element={simGuard("fluidpump", <FluidPump />)} />
      <Route path="/ippv" element={simGuard("ippv", <IPPV />)} />
      <Route path="/anaes-machine" element={simGuard("anaesmachine", <AnaesMachine />)} />
      <Route path="/drug-calc" element={simGuard("drugcalc", <DrugCalc />)} />
      <Route path="/triage" element={simGuard("triage", <Triage />)} />
      <Route path="/admission" element={simGuard("admission", <Admission />)} />
      <Route path="/clinpath" element={simGuard("clinpath", <ClinPath />)} />
      <Route path="/urinalysis" element={simGuard("urinalysis", <Urinalysis />)} />
      <Route path="/ward-rounds" element={simGuard("wardrounds", <WardRounds />)} />
      <Route path="/bloodwork" element={simGuard("bloodwork", <BloodReport />)} />
      <Route path="/sim/:simId" element={<SimGate><GenericSim /></SimGate>} />
      <Route path="/dental" element={simGuard("dental", <Dental />)} />
      <Route path="/drug-storage" element={simGuard("drugstorage", <DrugStorage />)} />
      <Route path="/nutrition" element={simGuard("nutrition", <Nutrition />)} />
      <Route path="/admin" element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<AdminOverview />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="subscribers" element={<AdminSubscribers />} />
        <Route path="teams" element={<AdminTeams />} />
        <Route path="billing" element={<AdminBilling />} />
        <Route path="audit" element={<AdminAudit />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="errors" element={<AdminErrors />} />
        <Route path="simulators" element={<AdminSimulators />} />
        <Route path="reading" element={<AdminReading />} />
      </Route>
      <Route path="*" element={<Navigate to="/sections" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      {/* Boot-time backend reachability check. Renders a fixed red banner if
          the frontend build can't reach a real VetSim backend — catches the
          "REACT_APP_BACKEND_URL baked to the wrong host" class of deploy bugs
          BEFORE the user hits sign-in and sees the generic "couldn't reach the
          server" message. Passive; sits outside the auth/billing providers so
          it still renders even if their initial fetches fail. */}
      <BackendHealthBanner />
      {/* Amber banner when navigator.onLine goes false. Supersedes the red
          backend-unreachable banner in that case — being offline is a
          temporary device-side state, not a broken app. */}
      <OfflineBanner />
      <AuthProvider>
        <SimProvider>
          <ContentProvider>
            <BillingProvider>
              <BrowserRouter>
                <ScrollToTop />
                <VisitTracker />
                <AdminConfigWarningBanner />
                <UpgradeBanner />
                {/* Headless background flusher — attempts to sync any queued
                    offline score-saves whenever the network is back and the
                    user is authenticated. Renders nothing. */}
                <ProgressQueueSync />
                <ErrorBoundary>
                  <AuthGate />
                </ErrorBoundary>
              </BrowserRouter>
              <Toaster richColors position="top-right" />
            </BillingProvider>
          </ContentProvider>
        </SimProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
